import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Divider,
  Collapse,
  Skeleton,
  Pagination,
  Box,
  useTheme,
} from "@mui/material";
import { useTranslation } from 'react-i18next';
import QuickSell from "../Common/QuickSell.component";
// import { useUserTokens } from "../../hooks/useUserTokens";
import { WalletsContext } from "../../state/context/wallets.context";
import { useNavigate, useParams } from "react-router-dom";

// import { useEffect, useState } from "react";
import axios from "axios";
import { transformToBTC, formatTokenAmount } from "../../utils/helpers";
import { useUserBalances } from "../../hooks";
import { useBitcoinPrice } from "../../state/context/bitcoin.context";

const useUserTokens = (principal, pageSize = 10) => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!principal) return;

    setLoading(true);
    axios
      .get(`https://api.odin.fun/v1/user/${ principal }/tokens?limit=10&page=${ page }&sort=holding_value%3Adesc`, {

      })
      .then((res) => {
        const raw = res.data.data || [];
        const tokens = raw.map((item) => ({
          ...item.token,
          totalAmount: item.balance,
        }));
        setData(tokens);
        setHasNextPage((res.data.count || 0) > page * pageSize);
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [principal, page, pageSize]);

  return {
    data,
    page,
    setPage,
    hasNextPage,
    loading,
    error,
  };
};


// const formatTokenAmount = (n) => {
//   if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
//   if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
//   if (n >= 1e3) return (n / 1e3).toFixed(2) + "K";
//   return n;
// };

const CollapsibleRow = ({ holding, balance }) => {
  const [open, setOpen] = useState(false);
  const timer = useRef();
  const theme = useTheme();
  const { state } = useContext(WalletsContext);
  const wallets = state.wallets || [];
  const { user } = useParams();
  const navigate = useNavigate();
  const isOwner = wallets.some((w) => w.userId === user);
  const { price: btcPrice } = useBitcoinPrice();

  const onEnter = () => {
    if (!isOwner) return;
    clearTimeout(timer.current);
    setOpen(true);
  };
  const onLeave = () => {
    if (!isOwner) return;
    timer.current = setTimeout(() => setOpen(false), 100);
  };

  useEffect(() => () => clearTimeout(timer.current), []);

  const marketCap = (((21000000 * (holding.price / 1e3)) / 1e8)).toFixed(4);

  return (
    <>
      <TableRow
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onClick={() => window.open(`/tokens/${ holding.id }`)}
        sx={{
          cursor: "pointer",
          "&:hover": {
            bgcolor: theme.palette.mode === 'light'
              ? 'rgba(0,0,0,0.04)'
              : 'rgba(255,255,255,0.1)'
          },
        }}
      >
        <TableCell>
          <Box display="flex" alignItems="center">
            <Avatar
              src={`https://images.odin.fun/token/${ holding.id }`}
              alt={holding.name}
              sx={{ width: 24, height: 24 }}
            />
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Typography>{holding.name}</Typography>
          </Box>
        </TableCell>
        <TableCell align="right">{balance && balance.airdroppedSent && balance.airdroppedSent > 0 ? formatTokenAmount(balance.airdroppedSent) : 0}</TableCell>
        <TableCell align="right">{balance && balance.airdroppedReceived && balance.airdroppedReceived > 0 ? formatTokenAmount(balance.airdroppedReceived) : 0}</TableCell>
        <TableCell align="right">{formatTokenAmount(holding.totalAmount)}</TableCell>
        <TableCell align="right">
          <Box>
            <Typography variant="body2">
              {marketCap} BTC
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ${(parseFloat(marketCap) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </Typography>
          </Box>
        </TableCell>

        <TableCell align="right">
          <Box>
            <Typography variant="body2">
              {transformToBTC((holding.totalAmount / 1e8))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ${(((holding.totalAmount / 1e8) / 1e11) * btcPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow onMouseEnter={onEnter} onMouseLeave={onLeave}>
        <TableCell colSpan={4} sx={{ p: 0, borderBottom: "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <QuickSell tokenId={holding.id} maxAmount={holding.totalAmount / 1e11} />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const UserHoldingsTable = ({ holdings, userId }) => {
  const ids = holdings.map((h) => h.id);
  const tokenids = encodeURIComponent(JSON.stringify(ids));
  const { data, loading, error } = useUserBalances(userId, 10, tokenids);
  return (
    <TableBody>
      {holdings.map((h) => {
        let balance;
        balance = data.find((b) => b.tokenid === h.id);
        return <CollapsibleRow holding={h} key={h.id} balance={balance} />;
      })}
    </TableBody>
  );
};

const UserHoldings = ({ userId }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data = [], loading, error, page, setPage, hasNextPage } =
    useUserTokens(userId, 10);

  if (loading) {
    return (
      <Skeleton
        variant="rectangular"
        sx={{
          borderRadius: 2,
          width: "100%",
          height: 300,
          m: 2,
        }}
      />
    );
  }
  if (error) {
    return (
      <Typography color="error" align="center" sx={{ m: 2 }}>
        Error: {error.toString()}
      </Typography>
    );
  }

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 3, m: 2 }}>
      <CardContent>
        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
          Holdings
        </Typography>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {[t('table.token'), t('table.airdropSent'), t('table.airdropReceived'), t('table.totalHoldings'), t('table.marketCap'), t('table.totalValue')].map((header, index) => {
                const isFirstColumn = index === 0;
                return (
                  <TableCell
                    key={header}
                    align={isFirstColumn ? "left" : "right"}
                    sx={{ fontWeight: "bold" }}
                  >
                    {header}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          {/* <TableBody>
            {data.map((h) => (
              <CollapsibleRow holding={h} key={h.id} />
            ))}
          </TableBody> */}
          <UserHoldingsTable userId={userId} holdings={data} />
        </Table>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination
            count={hasNextPage ? page + 1 : page}
            page={page}
            onChange={(e, v) => setPage(v)}
            color="primary"
            size="small"
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default UserHoldings;
